import { Question } from 'src/types/adaptive.ts'
import { AdaptiveMetrics, AdaptiveState } from '@/models/AdaptiveState';
import { QuizType, TestType, QuizMode } from '@/models/SharedTypes';
import type { DiagnosticSnapshot } from '@/types/backendCallables';

/** getUserProfile's `accessReason`, verbatim: why the server granted (or refused) access. */
export type AccessStatusReason =
    | 'lifetime'
    | 'subscription'
    | 'comp'
    | 'bonus'
    | 'trial'
    | 'none';

/** getUserProfile.trialWeek: null unless an Option-C trial week was opened (aswb-backend #477). */
export interface TrialWeekProof {
    openedAt: string;
    /** Local YYYY-MM-DD the week closed; null while it runs. */
    endedOn: string | null;
    /** Weakest module title; null unless two modules qualify. */
    weakest: string | null;
}

export interface User {
    id: string
    email: string
    profile: UserProfile | null;
    quizProgress: QuizProgress | null
    /** Legacy per-quiz map; writers no longer populate it (aswb-backend#513). Optional for old docs still carrying it. */
    quizzes?: Record<string, QuizProgress>
    testResults: Record<string, TestResult>
    wrongAnswers: WrongAnswer[]
    subscriptionStatus: string | ''
    isSubscribed?: boolean;
    /** One-time "access until you pass" purchase (Phase 2) — non-expiring; server-set via the Stripe webhook. */
    hasLifetimeAccess?: boolean;
    /**
     * Authoritative server-computed access decision (from getUserProfile).
     * Mirrors backend getUserProfile.hasFullAccess. Read this (or the
     * hasFullAccess() helper) instead of re-deriving access from raw fields.
     */
    hasFullAccess?: boolean;
    /**
     * Server-computed access reason from getUserProfile (trial week rule 1).
     * `reason === 'trial'` → /dashboard is the trial week; Practice is hidden.
     */
    accessStatus?: { reason: AccessStatusReason };
    adaptiveLearning?: AdaptiveMetrics;
    stripeCustomerId?: string;
    subscriptionId?: string;
    // Consent and email preferences
    consent?: {
        marketingEmailConsent: boolean;
        transactionalEmailConsent?: boolean;
        termsAcceptedAt: string;
        consentIpAddress?: string;
        lastUpdated: string;
    };
    /** ISO end of a study-email pause still in force ("not ready yet"), else null/absent. */
    studyPausedUntil?: string | null;
    emailPreferences?: {
        marketingEmailConsent?: boolean;
        studyReminders?: boolean;
        examAlerts?: boolean;
        productUpdates?: boolean;
        lastUpdated?: string;
    };
    // Stats for dashboard
    totalQuestionsAttempted?: number;
    correctAnswers?: number;
    studyStreak?: number;
    masteryLevel?: number;
    averageScore?: number;
    /** Server-computed LIFETIME accuracy (integer 0-100). Authoritative — prefer
     *  this over recomputing from correctAnswers / totalQuestionsAttempted. */
    accuracy?: number;
    /** Server-computed mastery as an integer percent (0-100). */
    masteryPercent?: number;
    scoreTrend?: {
        direction: 'up' | 'down';
        percentage: number;
    };
    completedTests?: number;
    totalStudyHours?: number;
    studyHoursTrend?: {
        direction: 'up' | 'down';
        percentage: number;
    };
    latestTestResult?: TestResult;
    totalWrongAnswers?: number;
    /** ISO account-creation timestamp from getUserProfile. Absent on the
     *  majority of legacy accounts, which never had a top-level createdAt. */
    createdAt?: string;
    // Trial-specific fields
    trialStartDate?: string;
    trialEndDate?: string;
    trialWeek?: TrialWeekProof | null;
    isTrialActive?: boolean;
    trialConvertedAt?: string;
    signupSource?: 'organic' | 'email' | 'google_ads' | 'social';
}

/**
 * Exam date entry for multi-exam support
 * Users can set different exam dates for each test type
 */
export interface ExamDateEntry {
  date: string;      // ISO date string
  setAt: string;     // When this date was set (ISO string)
}

/**
 * The five career-stage states an exam schedule can be in. Most ASWB candidates
 * have NO schedulable date (their date is gated by state-board approval), so the
 * schedule is status-FIRST — a date is one of five outcomes, not the only one.
 * Mirrors aswb-backend ExamStatus (feat/exam-schedule-model, PR #102).
 */
export type ExamStatus = 'scheduled' | 'approved' | 'waiting' | 'accruing' | 'exploring';

/**
 * Per-track exam schedule entry. Replaces the drifting legacy `examDate` +
 * `examDates[type]` fields with one status-first structure. Written server-side
 * via updateUserProfile({ examScheduleForTestType }); the server stamps updatedAt.
 */
export interface ExamScheduleEntry {
  status: ExamStatus;
  date?: string;          // 'YYYY-MM-DD' — present iff status === 'scheduled'
  targetWindow?: string;  // '2026-Q4' or '2026-11' — for 'approved' / 'waiting'
  hours?: { clinical: number; supervision: number }; // for 'accruing' (LCSW goal 3000/100)
  updatedAt: string;
}

/**
 * Prior-attempt ("retake") report gathered by the optional onboarding step.
 * Shape is PINNED by the backend contract (aswb-backend
 * feat/engine-prior-diagnostic-retake) — the engine reads it to seed a retaker's
 * first session on the areas their score report already flagged.
 */
/** Which ASWB blueprint the candidate's score report used. */
export type RetakeBlueprint = 'pre2026' | 'aug2026';

export interface RetakeReport {
  /** 1, 2, or 3, where 3 means "3 or more". Contract requires >= 1. */
  attempts: number;
  /**
   * Content-area CODES, never display labels. The merged contract types this as
   * a strict enum of 'I' | 'II' | 'III' | 'IV' and REJECTS anything else, for
   * BOTH blueprints: the backend matcher folds the 2026 areas onto these codes.
   */
  lowestAreas: string[];
  /** Missed the cut score narrowly. Halves the cold-start category bias. */
  nearMiss: boolean;
  /**
   * Which blueprint the score report used, so the backend can read `lowestAreas`
   * with the right area count. Optional: the backend field is landing now, and
   * until it does zod strips the key rather than rejecting the request.
   */
  blueprint?: RetakeBlueprint;
}

export interface UserProfile {
  name: string;
  targetExam?: TestType;
  examDate: string | null;  // Legacy: kept for backward compatibility (synced with targetExam's date)
  examDates?: Partial<Record<TestType, ExamDateEntry>>;  // Legacy: per-exam dates (superseded by examSchedule)
  examSchedule?: Partial<Record<TestType, ExamScheduleEntry>>;  // New: status-first per-track schedule
  nextScheduledExamMs?: number;  // Server-computed: soonest scheduled exam (epoch ms), or undefined
  examDatePromptDismissed?: boolean;  // Legacy: single dismissal flag
  examDatePromptsDismissed?: TestType[];  // New: per-exam dismissal tracking
  studyGoal: string;
  educationLevel?: EducationLevel;
  preferredStudyTime?: PreferredStudyTime;
  challengingTopics?: ChallengingTopic[];
  learningStyle?: LearningStyle;
  onboardingCompleted: boolean;
  lastUpdated: string;
  /** Prior-attempt report from the optional onboarding retake step. Absent for first-timers. */
  retakeReport?: RetakeReport;
  /** Homepage 6Q snapshot, copied onto the user after auth. Null until persisted. */
  diagnostic?: DiagnosticSnapshot | null;
  /** ASWB school report code, set from the dashboard program card. Never required. */
  schoolCode?: string | null;
}

export interface QuizProgress {
    quizId: string;
    currentQuestionIndex?: number;
    initialQuestionIndex?: number;
    score?: number;
    date?: string;
    wrongAnswers?: WrongAnswer[];
    questions?: Question[];
    questionIds?: string[];
    questionsAnswered?: number; // Number of questions answered (for progress tracking)
    lastUpdated: string;
    type?: QuizType;        // @deprecated use testType and mode instead
    testType: TestType;     // The specific test being taken (LCSW, LMSW, etc.)
    mode: QuizMode;         // How the quiz is being taken (practice, full, category)
    category: string;
    completed?: boolean;
    lastQuestionStartTime?: number;
    questionTimes?: { [questionId: string]: number };
    adaptiveState: AdaptiveState;
    lastSavedIndex?: number; // Track the last successfully saved question index
}

export interface TestResult {
    id: string;
    date: string;
    score: number;
    totalQuestions: number;
    percentage: number;
    questions?: Question[];
    questionIds: string[];
    wrongAnswers: WrongAnswer[];
    type: QuizType;
    category?: string;
    timeTaken?: number;
    adaptiveMetrics: AdaptiveMetrics;
    adaptiveState?: AdaptiveState;
}

export interface WrongAnswer {
    question: {
        id: string;
        category: string;
        question: string;
        correct_answer: string;
        selected_answer?: string;
        test_type?: string; // TestType from the quiz session
    };
    explanation: string;
    note?: string;
    date: string;
    timestamp?: number;
    isReviewed?: boolean;
    attempts: number; // Made required since we always provide a value
}

export enum StudyGoal {
    PASS_EXAM = "Pass the LCSW exam",
    IMPROVE_SKILLS = "Improve clinical skills",
    CAREER_ADVANCEMENT = "Career advancement"
}

export enum EducationLevel {
    BACHELORS = "Bachelor's in Social Work",
    MASTERS = "Master's in Social Work",
    OTHER = "Other related degree"
}

export enum WorkExperience {
    LESS_THAN_2 = "Less than 2 years",
    TWO_TO_5 = "2-5 years",
    FIVE_TO_10 = "5-10 years",
    MORE_THAN_10 = "More than 10 years"
}

export enum PreferredStudyTime {
    MORNING = "Morning",
    AFTERNOON = "Afternoon",
    EVENING = "Evening",
    NIGHT = "Night"
}

export enum ChallengingTopic {
    ETHICS = "Ethics",
    DIAGNOSIS = "Diagnosis",
    TREATMENT_PLANNING = "Treatment Planning",
    HUMAN_DEVELOPMENT = "Human Development",
    PSYCHOTHERAPY = "Psychotherapy",
    DIVERSITY = "Diversity and Cultural Competence",
    RESEARCH = "Research and Program Evaluation"
}

export enum LearningStyle {
    VISUAL = "visual",
    AUDITORY = "auditory",
    READING_WRITING = "reading_writing",
    KINESTHETIC = "kinesthetic"
}

// Display labels for learning styles
export const LearningStyleLabels: Record<LearningStyle, string> = {
    [LearningStyle.VISUAL]: "Visual",
    [LearningStyle.AUDITORY]: "Auditory",
    [LearningStyle.READING_WRITING]: "Reading/Writing",
    [LearningStyle.KINESTHETIC]: "Kinesthetic"
};